"use strict";

/**
 * DGF-3054 — puts a CSRF token on the express confirmation form.
 *
 * THE PROBLEM
 *
 * app_adyen_SFRA/.../js/adyen/checkout/helpers.js:111-124 createShowConfirmationForm builds a
 * NATIVE POST form with exactly five hidden inputs — additionalDetailsHidden, merchantReference,
 * orderToken, result, redirectUrl — and no CSRF token. Every express wallet posts it:
 *
 *   applepay.js:194   helpers.createShowConfirmationForm(this.showConfirmationAction)
 *   googlepay.js:283  helpers.createShowConfirmationForm(this.showConfirmationAction)
 *   paypal.js:142     this.helpers.createShowConfirmationForm(this.showConfirmationAction)
 *
 * and `showConfirmationAction` is destructured from window.showConfirmationAction, which
 * adyen/expressPaymentsMetadata.isml sets to Adyen-ShowConfirmationPaymentFromComponent. WP1 has
 * prepended csrf.validateRequest to that route, so without this the POST 403s on every surface and
 * express is broken end to end. The #adyen-token element only fixes the AJAX calls, because
 * commons/httpClient.js injects it into ajax data — a native form submit never goes near it.
 *
 * THE FIX
 *
 * app_adyen_SFRA cannot be edited, so the exported helpers object is wrapped at runtime.
 * module.exports there is an object literal of function references and every caller looks the
 * property up at call time, so replacing the property is enough — no vendor file changes, and the
 * class internals are untouched. The same technique is already used server-side: int_adyen_custom's
 * adyenHelper.js mutates the vendor singleton returned by module.superModule (Milestone 0 section 5).
 *
 * Injection happens as the form is created, which is the earliest moment it can happen and always
 * before any submit: both wallets call createShowConfirmationForm and only then reach
 * submitSuccessForm / onAdditionalDetails. The form is created lazily and only once
 * (createShowConfirmationForm returns early if #showConfirmationForm already exists), so the token
 * is (re)applied on every call rather than only when the element is new, and a retry refreshes the
 * value in place instead of appending a second input.
 *
 * The token comes from the #adyen-token element rendered by adyen/expressPaymentsToken.isml — the
 * same element and the same token as the AJAX path, so there is no second token mechanism. The
 * field NAME is read from that element too, because it is rendered from
 * CSRFProtection.getTokenName() and csrf.validateRequest reads the configured parameter name; it is
 * never hard-coded here.
 */

const expressDom = require("./expressDom");

const CONFIRMATION_FORM_ID = "showConfirmationForm";
const TOKEN_ELEMENT_ID = "adyen-token";
const PATCHED_FLAG = "sdExpressCsrfPatched";

/**
 * Reads the CSRF field name and value from the express token element.
 *
 * @param {Object} doc - document-like object
 * @returns {Object|null} { name, value }, or null when no usable token is present
 */
function readCsrfToken(doc) {
    if (!doc || typeof doc.getElementById !== "function") {
        return null;
    }
    const tokenElement = doc.getElementById(TOKEN_ELEMENT_ID);
    if (!tokenElement) {
        return null;
    }
    const name = typeof tokenElement.getAttribute === "function"
        ? tokenElement.getAttribute("name")
        : null;
    const value = tokenElement.value || (typeof tokenElement.getAttribute === "function"
        ? tokenElement.getAttribute("value")
        : null);
    if (!name || !value) {
        return null;
    }
    return { name: name, value: value };
}

/**
 * Makes sure the confirmation form carries the current CSRF token.
 *
 * Idempotent: the same input is reused and its value refreshed, so repeated calls — form creation,
 * a retried submission — cannot produce two token fields.
 *
 * @param {Object} doc - document-like object
 * @returns {boolean} whether the form now carries a token
 */
function ensureCsrfToken(doc) {
    if (!doc || typeof doc.getElementById !== "function") {
        return false;
    }
    const form = doc.getElementById(CONFIRMATION_FORM_ID);
    if (!form) {
        return false;
    }
    const token = readCsrfToken(doc);
    if (!token) {
        return false;
    }

    let input = typeof form.querySelector === "function"
        ? form.querySelector(`[name="${token.name}"]`)
        : null;
    if (!input) {
        input = doc.createElement("input");
        input.setAttribute("type", "hidden");
        input.setAttribute("name", token.name);
        form.appendChild(input);
    }
    input.value = token.value;
    input.setAttribute("value", token.value);
    return true;
}

/**
 * Puts every express surface on the page into its confirming state.
 *
 * Every surface, not just the one that was clicked: the mini bag and the cart page can both be in
 * the DOM at once, and the vendor helper gives us no indication of which wallet called it. Leaving
 * the other surface's buttons live would offer a second payment for an order already authorised.
 *
 * Uses the container class rather than expressDom.findExpressContainers, which needs a surface name
 * this hook does not have.
 *
 * @param {Object} doc - document-like object
 * @returns {void}
 */
function markSurfacesBusy(doc) {
    if (!doc || typeof doc.querySelectorAll !== "function") {
        return;
    }
    const containers = doc.querySelectorAll(".js-adyen-express");
    if (!containers || !containers.length) {
        return;
    }
    // A NodeList, so indexed rather than forEach — the DOM doubles used in tests return plain arrays
    // and older browsers do not implement NodeList.prototype.forEach.
    for (let i = 0; i < containers.length; i += 1) {
        expressDom.setSurfaceBusy(containers[i], true);
    }
}

/**
 * Wraps the vendor helpers object so every confirmation form it creates carries a token.
 *
 * @param {Object} helpers - the app_adyen_SFRA adyen/checkout/helpers module object
 * @param {Object} deps - injected dependencies ({ doc })
 * @returns {boolean} whether the patch is in place
 */
function patchCreateShowConfirmationForm(helpers, deps) {
    if (!helpers || typeof helpers.createShowConfirmationForm !== "function") {
        return false;
    }
    if (helpers[PATCHED_FLAG]) {
        // The module object is shared, and the mini bag fragment re-evaluates the bundle, so this
        // must not wrap itself repeatedly.
        return true;
    }

    const original = helpers.createShowConfirmationForm;
    helpers.createShowConfirmationForm = function createShowConfirmationFormWithCsrf(action) {
        const result = original(action);
        ensureCsrfToken(deps.doc);
        // The wallet has authorised by the time this runs and the confirmation form is about to be
        // posted, so this is the moment to tell the shopper something is happening. Driven from here
        // rather than from each orchestrator because every wallet on every surface funnels through
        // this one vendor helper (applepay.js:194, googlepay.js:283, paypal.js:142) — one hook covers
        // the PDP, the bag and the mini bag, and cannot be forgotten when a surface is added.
        //
        // Nothing clears it on success, deliberately: the next thing that happens is a native form
        // POST and a redirect to the confirmation page, so the state should persist until the page
        // goes away.
        //
        // WHAT HAPPENS ON FAILURE, since the comment previously here was wrong. It claimed "the
        // failure paths clear it — expressDom.setSurfaceError does so whenever it shows a message".
        // setSurfaceError does clear the busy state (expressDom.js:211-213), but NO code path shows a
        // message during a payment attempt: the only callers that pass a non-empty message are the
        // payment-methods-request failures at cartWallets.js:297 and productWallets.js:506, both of
        // which run long before a wallet sheet can open, and cartWallets.js:138 only ever clears. So
        // nothing calls it here, and the sentence described a mechanism that does not exist. Raised as
        // a false comment; traced instead:
        //
        //   - Almost every in-attempt failure NAVIGATES, which is why this was not visible in
        //     testing. Both vendor error handlers submit the confirmation form themselves after
        //     writing {error: true} into #result — applepay.js:100-112 and googlepay.js:163-171 — and
        //     the route they post to renders the error page. Apple Pay routes every non-Authorised
        //     result there (applepay.js:114-120 -> :203) and Google Pay routes every throw there
        //     (googlepay.js:303-306). The busy state persisting until the page goes away is correct
        //     on all of those, exactly as on success.
        //
        //   - TWO PATHS DO NOT NAVIGATE, and on those the spinner does turn indefinitely:
        //     (a) googlepay.js:295-302 — a refused payment. The form has been created at :283, so
        //         this state is set; then `actions.resolve({resultCode})` hands the component a
        //         non-Authorised code, `submitSuccessForm` is skipped by the :299 Authorised guard,
        //         and handleError is not reached because nothing threw and resultCode is truthy
        //         (the :280 guard only catches a MISSING resultCode). stopSpinner runs, the sheet
        //         closes, the page stays.
        //     (b) applepay.js:183-204 — onSubmit has no try/catch at all, so anything that throws
        //         between :194 and :203 (setOrderFormData on a malformed response,
        //         `this.helpers` being undefined at :196 on the PDP surface) becomes an unhandled
        //         rejection with the form built and never submitted.
        //
        // NOT FIXED HERE, and this is the reasoning rather than an omission:
        //
        //   - Clearing it on a timer was rejected outright. setSurfaceBusy(false) re-enables the
        //     buttons (_expressPayments.scss:511-516 is the pointer-events block), and this hook
        //     cannot tell (a) — where the shopper was NOT charged and re-enabling is desirable — from
        //     a slow confirmation POST, where the shopper HAS authorised and a second press is a
        //     second charge. Guessing wrong in that direction is unacceptable in a payment path, so
        //     the state stays set: fail closed, spinner turning, no possibility of a double charge.
        //   - Wrapping the failure handlers the way createShowConfirmationForm is wrapped is not
        //     possible. That works only because createShowConfirmationForm is a property of the
        //     `helpers` MODULE OBJECT, looked up at call time. handleError is a class member on each
        //     wallet — an instance field at applepay.js:100, a static at googlepay.js:163 — so it is
        //     not reachable through `helpers`; and neither of the two stuck paths calls it anyway.
        //
        // The real seam is the wallet components' own failure callbacks, which are configured by the
        // orchestrators that construct the wallets (cartWallets.js, productWallets.js) and not by
        // this file. Reported for those, not reached for from here.
        markSurfacesBusy(deps.doc);
        return result;
    };
    helpers[PATCHED_FLAG] = true;
    return true;
}

module.exports = {
    CONFIRMATION_FORM_ID: CONFIRMATION_FORM_ID,
    TOKEN_ELEMENT_ID: TOKEN_ELEMENT_ID,
    PATCHED_FLAG: PATCHED_FLAG,
    readCsrfToken: readCsrfToken,
    ensureCsrfToken: ensureCsrfToken,
    markSurfacesBusy: markSurfacesBusy,
    patchCreateShowConfirmationForm: patchCreateShowConfirmationForm
};
